import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { nextTick } from '@studiometa/js-toolkit/utils';
import { ScrollAnimation, FigureTwicpics } from '@studiometa/ui';
import { isDev } from '#js/config.js';
import AnchorScrollTo from '#js/atoms/AnchorScrollTo.js';
import ScrollReveal from '#js/atoms/ScrollReveal.js';
import Action from '#js/atoms/Action.js';
import RoomTypeItem from '#js/atoms/RoomTypeItem.js';
import MobileAccordion from '#js/molecules/MobileAccordion.js';
import Modal from '#js/molecules/Modal.js';
import Header from '#js/organisms/Header.js';
import Navbar from '#js/organisms/Navbar.js';
import VideoFigure from '#js/atoms/VideoFigure/VideoFigure.js';
import PageTransition from '#js/organisms/PageTransition/PageTransition.js';

/**
 * @typedef {{
 *   $children: {
 *     'a[href^="#"]': AnchorScrollTo[];
 *     'figure[data-component="Figure"]': FigureTwicpics[];
 *     VideoFigure: VideoFigure[];
 *     ScrollAnimation: ScrollAnimation[];
 *     ScrollReveal: ScrollReveal[];
 *     Action: Action[];
 *     RoomTypeItem: RoomTypeItem[];
 *     MobileAccordion: MobileAccordion[];
 *     Header: Header[];
 *     Navbar: Navbar[];
 *     PageTransition: PageTransition[];
 *     Modal: Modal[];
 *     Slider: Array<Promise<import('./molecules/Slider.js')>>;
 *     Video: Array<Promise<import('./molecules/Video.js')>>;
 *     Accordion: Array<Promise<import('./molecules/Accordion.js')>>;
 *     Gallery: Array<Promise<import('./organisms/Gallery.js')>>;
 *   }
 * }} AppProps
 */

/**
 * App class.
 * @augments {Base<AppProps>}
 */
class App extends Base {
  /**
   * App config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    log: isDev(),
    name: 'App',
    refs: ['hero'],
    components: {
      'a[href^="#"]': AnchorScrollTo,
      'figure[data-component="Figure"]': FigureTwicpics,
      VideoFigure,
      ScrollAnimation,
      ScrollReveal,
      Action,
      RoomTypeItem,
      MobileAccordion,
      Header,
      Navbar,
      PageTransition,
      Modal,
      Slider: (app) => importWhenVisible(() => import('./molecules/Slider.js'), 'Slider', app),
      Video: (app) => importWhenVisible(() => import('./molecules/Video.js'), 'Video', app),
      Accordion: (app) =>
        importWhenVisible(() => import('./molecules/Accordion.js'), 'Accordion', app),
      Gallery: (app) => importWhenVisible(() => import('./organisms/Gallery.js'), 'Gallery', app),
    },
  };

  /**
   * Navbar
   * @returns {Navbar}
   */
  get $Navbar() {
    return this.$children.Navbar[0] ?? null;
  }

  /**
   * Header
   * @returns {Header}
   */
  get $Header() {
    return this.$children.Header[0] ?? null;
  }

  /**
   * Log a nice message when app is ready.
   * @returns {void}
   */
  mounted() {
    nextTick(() => {
      setTimeout(() => {
        document.documentElement.classList.remove('preload');
      }, 1000);
    });
  }

  /**
   * On video figure unmute
   * @param {number} index
   */
  onVideoFigureUnmute(index) {
    this.$children.VideoFigure.filter(
      (instance) => this.$children.VideoFigure[index] !== instance,
    ).forEach((instance) => instance.mute());
  }
}

export default createApp(App, document.documentElement);
